
import Vue from 'vue';
import isMobileView from '@root/common/utils/isMobileView';
import { PodcastAsArticle, Podcast, PlaylistType, PodcastCategory } from '@root/modules/podcast/types/podcast';
import ContentImage from '@core/components/UI/ContentImage.vue';
import SharePopup from '@root/modules/podcast/components/fragments/SharePopup.vue';
import { CurrentlyPlaying } from '@root/modules/podcast/types/player';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import { CustomCrop } from '@root/common/types/fragment';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import { radioStreams } from '../config/podcast.config';
import { faArrowUpFromBracket } from '@delfi.lt/fa-kit/icons/modules/classic/regular';

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

interface ShNavigator extends Navigator {
  share: (data?: ShareData) => Promise<void>;
}

interface ShWindow extends Window {
  [index: string]: any;
  navigator: ShNavigator;
}

declare let window: ShWindow;

interface RadioStream {
  id: number;
  url: string;
}

interface Props {
  category: PodcastCategory | null;
  episode: Podcast | null;
}

interface Data {
  isMobileView: boolean;
  playlistUrl: string | null;
  showSharePopup: boolean;
  radioStreams: RadioStream[];
}

interface Methods {
  getCustomerPlaylist: () => void;
  getCropperData(): Record<string, unknown> | null;
  getCustomCrop(ratio: string): CustomCrop | undefined;
  getFocuspoint(): Record<string, unknown>;
  setActivePlaylistType: (value: PlaylistType) => void;
  share: (item: PodcastAsArticle) => Promise<void>;
}

interface Computed {
  activePlaylistType: PlaylistType;
  currentlyPlaying: CurrentlyPlaying | null;
  customerPlaylist: Podcast[];
  customerToken: string | null;
  mobileAds: string[] | null;
}

const pictureSrcset: PictureSrcset = { lg: { w: 560, h: 560, r: '1:1' }, sm: { w: 420, h: 420, r: '1:1' }, xs: { w: 280, h: 280, r: '1:1' } };
const sizeList: ['lg', 'sm', 'xs'] = ['lg', 'sm', 'xs'];

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ContentImage,
    SharePopup,
    AdFormBanner,
  },
  props: {
    category: {
      type: Object,
      required: false,
      default: null,
    },
    episode: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isMobileView: false,
      playlistUrl: null,
      showSharePopup: false,
      radioStreams,
      faArrowUpFromBracket,
    };
  },
  computed: {
    activePlaylistType() {
      return this.$store.state.podcast.activePlaylistType;
    },
    currentlyPlaying() {
      return this.$player.state.currentlyPlaying;
    },
    customerPlaylist() {
      return this.$store.state.podcast.customerPlaylist;
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
    mobileAds() {
      const ads = this.$store.getters['adForm/getAdsPlacements'];

      return ads?.mobile?.mid || null;
    },
  },
  methods: {
    async getCustomerPlaylist() {
      await this.$store.dispatch('podcast/setCustomerPlaylist');
    },
    getCustomCrop(ratio) {
      const image = this.episode?.metaImage;
      if (!image) {
        return undefined;
      }

      const customCrops = image!.attributes?.cropperData?.customCrops || [];

      return customCrops.find((crop: { ratio: string }) => crop.ratio === ratio);
    },
    getFocuspoint() {
      const cropperProps: Record<string, unknown> = {};

      const image = this.episode?.metaImage;

      const focuspoint = image?.attributes?.cropperData?.focuspoint;
      if (focuspoint?.x !== undefined && focuspoint?.x !== null) {
        cropperProps.fx = focuspoint.x;
      } else {
        cropperProps.fx = '0.5';
      }

      if (focuspoint?.y !== undefined && focuspoint?.y !== null) {
        cropperProps.fy = focuspoint.y;
      } else {
        cropperProps.fy = '0.5';
      }

      return cropperProps;
    },
    getCropperData() {
      if (!this.episode) {
        return null;
      }

      const cropperData: Record<string, unknown> = {};

      for (const size of sizeList) {
        const _pictureSrcset = pictureSrcset;
        if (_pictureSrcset?.[size]) {
          const sizeSrcset = _pictureSrcset[size];

          const customCrop = this.getCustomCrop(sizeSrcset?.r || '1:1');

          if (customCrop) {
            cropperData[size] = { ...sizeSrcset, customCrop };
          } else {
            cropperData[size] = sizeSrcset;
          }
        }
      }

      const image = this.episode?.metaImage;
      if (!image) {
        return null;
      }

      return {
        ...cropperData,
        ...(image!.attributes?.cropperData || {}),
        base: this.getFocuspoint(),
      };
    },
    setActivePlaylistType(value) {
      this.$store.dispatch('podcast/setActivePlaylistType', value);
    },
    async share(item) {
      if (!isMobileView()) {
        this.showSharePopup = true;
        return;
      }

      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      const title = item.content.title.text;
      const text = item.content.lead?.text;
      const url = buildPodcastUrl({ article: item, domain: 'www.delfi.lt/fone', fakeDomainEnabled });

      if (window.navigator.share) {
        await window.navigator.share({
          title,
          text,
          url,
        });
      } else if (window.AndroidWebAppMobileAPI && window.AndroidWebAppMobileAPI.sharePage) {
        window.AndroidWebAppMobileAPI.sharePage(url, title, text);
      }
    },
  },
  async mounted() {
    this.isMobileView = isMobileView();

    if (this.customerToken) {
      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.getCustomerPlaylist();
    }

    if (this.episode) {
      if (this.category) {
        // Setup playlist for big player!
        const playlist = [];
        for (const item of this.category?.articles.items) {
          playlist.push({
            ...item,
            streamUrl: radioStreams.find((stream) => stream.id === item.id)?.url,
          });
        }

        this.$store.dispatch('podcast/setPlaylist', playlist);
      }

      const streamUrl = radioStreams.find((stream) => stream.id === this.episode?.id)?.url;
      if (streamUrl) {
        await this.$player.setup({ ...this.episode, streamUrl });
      }
    }
  },
});
